import * as jwt from 'jsonwebtoken';

import { IUserJwt, UserClaim, UserRole } from '@repo/types-client';

/**
 * Decodes a JWT token.
 * @function decodeToken
 * @template T - The type of the decoded token.
 * @param {string | undefined} token - The JWT token to decode.
 * @returns {T | undefined} The decoded token or undefined if the token is not provided.
 */
const decodeToken = <T = unknown>(token: string | undefined): T | undefined =>
  !token ? undefined : (jwt.decode(token) as T);

/**
 * Checks if the user has an admin role.
 * @function isAdmin
 * @param {IUserJwt | undefined} context - The user JWT context.
 * @returns {boolean} True if the user has an admin role, false otherwise.
 */
const isAdmin = (context: IUserJwt | undefined): boolean =>
  context ? context.roles && context.roles.includes('admin') : false;

/**
 * Checks if the user has a specific claim.
 * @function hasClaim
 * @param {IUserJwt | undefined} context - The user JWT context.
 * @param {UserClaim} claim - The claim to check.
 * @returns {boolean} True if the user has the claim, false otherwise.
 */
const hasClaim = (context: IUserJwt | undefined, claim: UserClaim): boolean =>
  isAdmin(context) ||
  (context && context.claims && context.claims.includes(claim))
    ? true
    : false;

/**
 * Checks if the user has a specific role.
 * @function hasRole
 * @param {IUserJwt | undefined} context - The user JWT context.
 * @param {UserRole} role - The role to check.
 * @returns {boolean} True if the user has the role, false otherwise.
 */
const hasRole = (context: IUserJwt | undefined, role: UserRole): boolean =>
  isAdmin(context) || (context && context.roles && context.roles.includes(role))
    ? true
    : false;

export { decodeToken, isAdmin, hasClaim, hasRole };
